import {
  Dispatch,
  SetStateAction,
  createContext,
  ReactNode,
  ReactElement,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";

import { useAccount, useMsal } from "@azure/msal-react";
import { AuthError, EventType, InteractionType } from "@azure/msal-browser";
import { EventMessage } from "@azure/msal-browser/dist/event/EventMessage";

import { forgotPasswordRequest, loginRequest } from "./AzureProvider";
import globalAxios, { AxiosError } from "axios";

const authRefreshCheckStatusCodes = [401, 403, 504];

const MAX_REAUTH_ATTEMPTS = 1;

type AuthContextValues = {
  login(): void;
  logout(): void;
  accessToken?: string;
  setAccessToken: Dispatch<SetStateAction<string | undefined>>;
  username: string;
  userId?: string;
};

const AuthContext = createContext<AuthContextValues>({
  login: () => {
    return;
  },
  logout: () => {
    return;
  },
  setAccessToken: () => {
    return;
  },
  username: "",
  userId: "",
});

class ReauthError extends Error {}

const AuthProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [accessToken, setAccessToken] = useState<string>();
  const [username, setUsername] = useState<string>("");
  const [userId, setUserId] = useState<string>();

  useEffect(() => {
    globalAxios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${accessToken}`;
  }, [accessToken]);

  useEffect(() => {
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/a7f1995767101d0a93ed212b373d46ea7131bd3c/samples/msal-react-samples/b2c/src/App.jsx#L40
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      if (
        message.eventType === EventType.LOGIN_FAILURE &&
        message.interactionType === InteractionType.Redirect
      ) {
        if ((message.error?.message.indexOf("AADB2C90118") ?? -1) > -1) {
          instance.loginRedirect(forgotPasswordRequest).catch(console.error);
        }
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  const logout = useCallback(() => {
    instance
      .logoutRedirect({
        account: instance.getActiveAccount(),
      })
      .then(() => {
        setAccessToken(undefined);
        setUsername("");
      })
      .catch(console.error);
  }, [instance]);

  const login = useCallback(() => {
    instance.loginRedirect(loginRequest).catch(console.error);
  }, [instance]);

  const refreshToken = useCallback(
    () =>
      account
        ? instance
            .acquireTokenSilent({
              ...loginRequest,
              account,
            })
            .then(({ accessToken: _accessToken, account: _account }) => {
              setAccessToken(_accessToken);
              setUsername(_account?.username ?? "username undefined");
              setUserId(_account?.localAccountId);
              return _accessToken;
            })
        : Promise.reject(),
    [account, instance]
  );

  useEffect(() => {
    if (account) {
      refreshToken().catch(logout);
    }
  }, [account, logout, refreshToken]);

  const reauthAttempts = useRef(0);

  // The accessToken is only good for so long, attempt to refresh if certain status codes are returned.
  useEffect(() => {
    const responseHandlerId = globalAxios.interceptors.response.use(
      undefined,
      (error: AxiosError) =>
        authRefreshCheckStatusCodes.includes(error.response?.status ?? 0)
          ? refreshToken()
              .then((_accessToken: string) => {
                if (reauthAttempts.current >= MAX_REAUTH_ATTEMPTS) {
                  reauthAttempts.current = 0;
                  throw new ReauthError("Unable to fetch data");
                }
                reauthAttempts.current += 1;
                if (!error.config) {
                  throw new ReauthError("Invalid or missing config");
                }
                error.config.headers[
                  "Authorization"
                ] = `Bearer ${_accessToken}`;
                error.config.baseURL = undefined;
                return globalAxios.request(error.config);
              })
              .catch((err: AuthError | ReauthError) => {
                if (err instanceof AuthError) {
                  logout();
                  return;
                }
                throw err;
              })
          : Promise.reject(error)
    );

    return () => {
      globalAxios.interceptors.response.eject(responseHandlerId);
    };
  }, [refreshToken, logout]);

  const values = useMemo(
    () => ({
      login,
      logout,
      accessToken,
      setAccessToken,
      username,
      userId,
    }),
    [login, logout, accessToken, setAccessToken, username, userId]
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValues => {
  return useContext(AuthContext);
};

export default AuthProvider;
